import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import meetingService from '../../services/meetingService';
import { notify } from '../CustomToast';
import './ProposedTimeRequestsModal.css';
import { useI18n } from '../../i18n';
import ProposalListSkeleton from './ProposalListSkeleton';

const ProposedTimeRequestsModal = ({ meeting, onClose }) => {
  const { t } = useI18n();
  const [allProposals, setAllProposals] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actionInProgress, setActionInProgress] = useState(false);

  // Fetch proposals on mount and when filter changes
  useEffect(() => {
    fetchProposals();
  }, [statusFilter]);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const response = await meetingService.listProposals(meeting.id);

      if (response.status === true && response.data && response.data.proposals) {
        const allData = response.data.proposals;
        setAllProposals(allData);
        const filtered = allData.filter(p => p.status === statusFilter);
        setProposals(filtered);
        setSelectedProposal(null);
      }
    } catch (error) {
      notify('error', t('common.messages.error_loading_proposals') || 'Error loading proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedProposal) return;

    try {
      setActionInProgress(true);
      await meetingService.acceptProposal(meeting.id, selectedProposal.id);

      notify('success', t('pages.meetings.proposal_accepted') || 'Proposal accepted');
      await fetchProposals();
    } catch (error) {
      const message =
        error?.message ||
        t('common.messages.error_updating_proposal') ||
        'Error accepting proposal';
      notify('error', message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleReject = async () => {
    if (!selectedProposal) return;

    try {
      setActionInProgress(true);
      await meetingService.rejectProposal(meeting.id, selectedProposal.id);

      notify('success', t('pages.meetings.proposal_rejected') || 'Proposal rejected');
      await fetchProposals();
    } catch (error) {
      const message =
        error?.message ||
        t('common.messages.error_updating_proposal') ||
        'Error rejecting proposal';
      notify('error', message);
    } finally {
      setActionInProgress(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const content = (
    <div className="ptrm-overlay" onClick={onClose}>
      <div className="ptrm-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Gradient Header */}
        <div className="ptrm-header">
          <h2 className="ptrm-title">{t('pages.meetings.time_proposals') || 'Time Proposals'}</h2>
          <button className="ptrm-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div className="ptrm-body">
          {/* Left Panel - Request List */}
          <div className="ptrm-left-panel">
            <div className="ptrm-filter-tabs">
              {['pending', 'accepted', 'rejected'].map(status => (
                <button
                  key={status}
                  className={`ptrm-filter-tab ${statusFilter === status ? 'active' : ''}`}
                  onClick={() => setStatusFilter(status)}
                >
                  {t(`common.status.${status}`) || status}
                  <span className="ptrm-count-badge">
                    {allProposals.filter(p => p.status === status).length}
                  </span>
                </button>
              ))}
            </div>

            <div className="ptrm-list">
              {loading ? (
                <ProposalListSkeleton />
              ) : proposals.length === 0 ? (
                <div className="ptrm-empty">{t('common.empty_states.no_proposals') || 'No proposals'}</div>
              ) : (
                proposals.map(proposal => (
                  <div
                    key={proposal.id}
                    className={`ptrm-request-item ${selectedProposal?.id === proposal.id ? 'selected' : ''}`}
                    onClick={() => setSelectedProposal(proposal)}
                  >
                    <div className="ptrm-request-header">
                      <span className="ptrm-proposer-name">{proposal.proposed_by?.name}</span>
                      <span className={`ptrm-status-badge ptrm-status-${proposal.status}`}>
                        {t(`common.status.${proposal.status}`) || proposal.status}
                      </span>
                    </div>
                    <div className="ptrm-request-time">
                      {formatDateTime(proposal.proposed_start_at)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Details */}
          <div className="ptrm-right-panel">
            {selectedProposal ? (
              <>
                {/* Proposer Info */}
                <div className="ptrm-section">
                  <h3>{t('pages.meetings.proposed_by') || 'Proposed by'}</h3>
                  <div className="ptrm-user-card">
                    <div className="ptrm-avatar">
                      {selectedProposal.proposed_by?.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="ptrm-user-info">
                      <div className="ptrm-user-name">{selectedProposal.proposed_by?.name}</div>
                      <div className="ptrm-user-email">{selectedProposal.proposed_by?.email}</div>
                    </div>
                  </div>
                </div>

                {/* Time Comparison */}
                <div className="ptrm-section">
                  <h3>{t('pages.meetings.time_comparison') || 'Time Comparison'}</h3>

                  <div className="ptrm-time-block">
                    <div className="ptrm-time-label">{t('pages.meetings.current_time') || 'Current Time'}</div>
                    <div className="ptrm-time-value">
                      {formatDateTime(selectedProposal.current_meeting_start_at)} - {formatDateTime(selectedProposal.current_meeting_end_at)}
                    </div>
                  </div>

                  <div className="ptrm-time-block">
                    <div className="ptrm-time-label">{t('pages.meetings.proposed_time') || 'Proposed Time'}</div>
                    <div className="ptrm-time-value">
                      {formatDateTime(selectedProposal.proposed_start_at)} - {formatDateTime(selectedProposal.proposed_end_at)}
                    </div>
                  </div>
                </div>

                {/* Message */}
                {selectedProposal.message && (
                  <div className="ptrm-section">
                    <h3>{t('common.message') || 'Message'}</h3>
                    <div className="ptrm-message">{selectedProposal.message}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="ptrm-no-selection">
                {t('pages.meetings.select_proposal') || 'Select a proposal to view details'}
              </div>
            )}
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="ptrm-footer">
          <button className="ptrm-btn-secondary" onClick={onClose}>
            {t('common.actions.close') || 'Close'}
          </button>
          {selectedProposal?.status === 'pending' && (
            <>
              <button
                className="ptrm-btn-reject"
                onClick={handleReject}
                disabled={actionInProgress}
              >
                {actionInProgress ? t('common.actions.processing') || 'Processing...' : t('common.actions.reject') || 'Reject'}
              </button>
              <button
                className="ptrm-btn-accept"
                onClick={handleAccept}
                disabled={actionInProgress}
              >
                {actionInProgress ? t('common.actions.processing') || 'Processing...' : t('common.actions.accept') || 'Accept'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default ProposedTimeRequestsModal;
